import { Button } from '@/components/Button'

type UpdateNotificationCardProps = {
  onReload: () => void
  onDismiss: () => void
}

/**
 * The "New version available" banner both shells show: the legacy
 * `UpdateNotificationBanner` and the new shell's `NewVersionBanner` own when it
 * appears; this owns how it looks.
 */
export const UpdateNotificationCard = ({
  onReload,
  onDismiss,
}: UpdateNotificationCardProps) => (
  <div
    className='fixed top-5 left-1/2 -translate-x-1/2 z-9999 max-w-[620px] w-[calc(100%-32px)] rounded-2xl border border-white/10 bg-secondary/90 backdrop-blur-lg shadow-lg animate-update-banner-in'
    role='alert'
    aria-live='polite'
    data-testid='update-notification-banner'
  >
    <div className='relative flex items-center gap-4 px-5 py-4 pr-14'>
      <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-accent/40 bg-accent/10 text-accent shadow-inner'>
        <svg
          width='22'
          height='22'
          viewBox='0 0 22 22'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          aria-hidden='true'
        >
          <path
            d='M11 20.1667C15.0619 20.1667 18.4167 16.8118 18.4167 12.75C18.4167 8.6882 15.0619 5.33337 11 5.33337C6.9382 5.33337 3.58337 8.6882 3.58337 12.75C3.58337 16.8118 6.9382 20.1667 11 20.1667Z'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M11 9.625V13.0417L13.0625 15.1042'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </div>

      <div className='flex-1 min-w-0 space-y-1 text-white'>
        <div className='truncate text-xl font-semibold leading-6'>
          New version available
        </div>
        <div className='text-sm leading-5 text-white/80'>
          An improved version of MyAssetMap is ready. Reload now to upgrade to
          the latest experience.
        </div>
      </div>

      <div className='flex shrink-0 items-center gap-2'>
        <Button
          onClick={onReload}
          variant='primary'
          size='sm'
          label='Reload'
          aria-label='Reload and upgrade to new version'
          className='h-9! px-4! text-sm font-semibold shadow-sm transition-transform duration-200 hover:scale-[1.02]'
        />
      </div>

      <Button
        onClick={onDismiss}
        variant='tertiary'
        size='sm'
        aria-label='Dismiss notification'
        iconProps={{ icon: ['fas', 'times'], size: 'sm' }}
        className='absolute top-3 right-3 h-8! w-8! px-0! py-0! text-white/70 hover:text-white focus:ring-accent focus:ring-offset-2 focus:ring-offset-secondary'
      />
    </div>
  </div>
)
